/**
 * Landing — the signed-out entry point (phone).
 * `design/GT Mobile Crew.dc.html` · `data-screen-label="Landing"`.
 *
 * Rendered without header or bottom nav: `headerVM().authed` is false here, the
 * same frame rule `login` and `reset` use.
 *
 * Two doors and no third. There is no crew browser and no request-to-join —
 * an invite link is the only way into somebody else's crew, which is why the
 * login form's secondary link says START A PARTY rather than "start or join"
 * (DESIGN-CHANGELOG 2026-08-18).
 *
 * NO PITCH LINE, NO FEATURE LINES, NO FOOTER — AND THAT IS THE DESIGN.
 *
 * The 2026-08-18 changelog entry describes all three and even quotes one of
 * them, so the obvious reading of the empty divs in the mirrors is that the
 * copy got lost in an export. It did not: Marcus deleted it because he did not
 * think it looked good (confirmed 2026-08-19), and the change was never written
 * back into the changelog. The MIRRORS are the live spec and they are right;
 * the changelog paragraph is stale.
 *
 * So: do not "restore" it. If it comes back it will come back as a design edit
 * with its own changelog entry.
 */

import { PixelButton } from '../components/PixelButton.tsx';
import { QuarterIcon } from '../components/QuarterIcon.tsx';
import { useQd } from '../store/index.ts';
import { landingVM } from '../store/selectors.ts';

export default function Landing(): JSX.Element {
  const s = useQd();
  const v = landingVM(s);
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '34px 16px 26px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <QuarterIcon u={2.5} />
        <div
          style={{
            fontFamily: 'var(--gt-font-pixel)',
            fontSize: '16px',
            color: 'var(--gt-text)',
            lineHeight: 1.6,
            marginTop: '16px',
          }}
        >
          GAMES TONIGHT<span style={{ color: 'var(--acc2)' }}>?</span>
        </div>
      </div>

      {v.crewless ? (
        <div
          style={{
            fontFamily: 'var(--gt-font-mono)',
            fontSize: '10px',
            lineHeight: 1.7,
            letterSpacing: '.04em',
            color: 'var(--gt-muted)',
            textAlign: 'center',
            marginTop: '20px',
          }}
        >
          {v.noteTxt}
        </div>
      ) : null}

      {v.signedIn ? (
        <div
          style={{
            fontFamily: 'var(--gt-font-mono)',
            fontSize: '9px',
            lineHeight: 1.8,
            letterSpacing: '.06em',
            textAlign: 'center',
            marginTop: '18px',
          }}
        >
          <div style={{ color: 'var(--gt-muted-2)' }}>{v.whoLbl}</div>
          <div style={{ color: 'var(--gt-text-5)', overflowWrap: 'anywhere' }}>{v.whoTxt}</div>
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '24px' }}>
        <PixelButton
          label="START A PARTY ▸"
          onClick={s.openWizard}
          fs="9.5"
          pad="16px 16px"
          center
        />
        <PixelButton
          label={v.altLbl}
          variant="outline"
          onClick={() => (v.crewless ? s.signOut() : s.nav('login'))}
          fs="9.5"
          pad="16px 16px"
          center
        />
      </div>
    </div>
  );
}
